
import React, { useEffect, useState } from 'react';
import PingStatusTooltip from "../../../../Components/PingStatus/PingStatusTooltip";
import { Box, IconButton, styled, Typography } from '@mui/material';
import GenericDraggableTableView from '../../../../Features/Table/GenericDraggableTable';
import CustomIcon from '../../../../Components/CustomIcon';
import CartLoader from '../../../../Helper/Loaders/CartLoader';
import Lottie from 'react-lottie';
import PurpleBlinkingLottie from '../../../../Components/Lotties/PurpleBlinkingLottie.json';
import { useTranslation } from 'react-i18next';
import { getColumnsConfig } from '../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations';
import { MAX_COLUMN_SELECTION_COUNT, multiplyFactors } from '../../../../Helper/DigitalTwin/DataFile';
import { IDDisplayBox } from '../../../../Theme/DigitalTwinStyles';
import { formatSocValue } from '../../../../Helper/DigitalTwin/DigitalTwinHelper';
import { StatusDot } from '../../../../Components/StyledComponent';
import { getBatteryColumns, getBatteryColumnsData } from '../../../../Api/DigitalTwin';
import { toTitleCase } from '../../../../Helper/Formatters/TextFormatter';
import { usePagesContent, useBaseEntityIdLabel, useGlobalConfig } from '../../../../Hooks/usePagesContent';
import { getVitalsIdDisplayConfig, resolveBatteryIdDisplay, resolveDeviceIdLabel } from '../../../../Helper/DigitalTwin/vehicleIdentifier';

const TableContainer = styled(Box)(({ theme }) => ({
    marginTop: '20px',
    width: '100%',
    minHeight: '200px',
    '& .ant-table-cell-with-append': {
        display: 'flex',
        alignItems: 'center'
    }
}))

const customRenderers = {
    live_name: (record, config) => {
        const indent = record.hierarchyLevel === 'cell' ? 35 : 0;
        const isActive = record.pingStatus?.toLowerCase() === 'active'
        return (
            <div style={{ paddingLeft: indent }}>
                {['battery'].includes(record.hierarchyLevel) && (
                    <PingStatusTooltip online={isActive}>
                        <StatusDot color={isActive ? '#09B820' : '#8F8F8F'} />
                    </PingStatusTooltip>
                )}
                <Typography variant="fw400s14lh24c0061CA" sx={{ "color": "#3D4B5A !important" }}>
                    {record[config.dataIndex]}
                </Typography>
            </div>
        );
    },

    'live_soc': (record, config) => {
        let soc = record[config.dataIndex]
        return (<Box sx={{ textAlign: 'right', color: soc && soc < 30 ? '#B50500' : '' }}>{formatSocValue(soc)}</Box>)
    }
};

const applyFormatting = (key, value, defaultValue) => {
    if (typeof value !== 'number') {
        return defaultValue
    }

    let multiplyFactor = multiplyFactors[key] || 1
    let formattedValue = (value * multiplyFactor)

    return formattedValue

}

// Keys a row carries for the renderers rather than for display. hierarchyLevel is the
// node's level and is compared against lowercase names ('battery', 'cell') by
// customRenderers.live_name and by every other consumer in the app, so title-casing it
// silently disabled those checks: the battery row lost its StatusDot and cell rows their
// indent, because ['battery'].includes('Battery') is false.
const UNFORMATTED_ROW_KEYS = ['hierarchyLevel'];

// Rendering types whose value is an identifier, so its case must survive untouched.
// toTitleCase lowercases the whole string before recapitalising each word, which turned
// the "Unit ID " column (dataIndex displayID, text-with-copy) from MK27220202605005513_C1
// into Mk27220202605005513_c1 - a serial that no longer matched the one in the page header
// or anything the user could search or paste back.
const CASE_SENSITIVE_RENDERING_TYPES = ['text-with-copy'];

/**
 * Merge one node's values into a row keyed by column title.
 *
 * Several entity levels can declare the same column title under different attribute
 * names: "Max Temperature (C)" is `temperature` on battery, `maxTempCell` on pack and
 * module, and `stringMaxTempCell` on string. Columns are collapsed by dataIndex, so all
 * of those survive as separate configs writing the same title, while a node only ever
 * carries the keys of its own level.
 *
 * Writing unconditionally let the last config win, so a level whose key came first had
 * its value blanked out by a level that had none - which is why the battery row showed
 * "-" for Max Temperature even though the API returned temperature: 24.0. A config with
 * no value may therefore no longer overwrite a value already written for that title.
 *
 * Precedence when two configs sharing a title BOTH have a value is still last-one-wins,
 * in the order the columns API returned them. A node carries the keys of one level only,
 * so that cannot happen with today's data; it would take two attributes of the same
 * entity sharing an attributeDisplayName (say a battery-level extended "Max Temperature"
 * alongside the core `temperature`). Give this an explicit rule before allowing that.
 *
 * Exported for unit tests.
 */
export const mergeRowValuesByTitle = (columnConfigs, enriched) => {
    const transformed = {};

    Object.keys(columnConfigs).forEach((cfgKey) => {
        const config = columnConfigs[cfgKey];
        const title = config?.title || cfgKey;
        const rawValue = enriched.hasOwnProperty(cfgKey) ? enriched[cfgKey] : null;
        const hasValue = rawValue !== '' && rawValue != null;

        if (!hasValue && transformed[title] !== undefined) {
            return;
        }

        const renderingType = config?.valueRenderingType;

        if (renderingType === 'timestamp') {
            // Passed through even when empty: the timestamp renderer turns that into
            // "Not Available" itself.
            transformed[title] = rawValue;
        } else if (CASE_SENSITIVE_RENDERING_TYPES.includes(renderingType)) {
            transformed[title] = hasValue ? rawValue.toString() : '-';
        } else {
            transformed[title] = hasValue ? toTitleCase(rawValue.toString()) : '-';
        }
    });

    // Keep any extra keys from enriched that aren't in columnConfigs
    Object.keys(enriched).forEach((key) => {
        if (!columnConfigs[key]) {
            if (UNFORMATTED_ROW_KEYS.includes(key)) {
                transformed[key] = enriched[key];
                return;
            }
            const value = enriched[key] !== '' && enriched[key] != null ? enriched[key] : '-';
            transformed[key] = toTitleCase(value.toString());
        }
    });

    return transformed;
};

const enrichBatterySystem = (columnConfigs, nodes, level = 0) => {
    // Sort nodes by sequence if available
    const sortedNodes = nodes?.slice().sort((a, b) => {
        if (a.sequence !== undefined && b.sequence !== undefined) {
            return a.sequence - b.sequence;
        }
        // Fall back to systemID sorting
        const idA = parseInt(a.systemID);
        const idB = parseInt(b.systemID);
        return idA - idB;
    }) || [];

    return sortedNodes.map((node, index) => {
        const nodeInfo = node.info || {}
        const enriched = {
            ...nodeInfo,
            key: node.systemID,
            name: node.name,
            displayID: node.displayID,
            hierarchyLevel: node.level,
            level,
        };

        // Map keys to their columnConfig titles
        // We want to convert keys into titles so that attributes with common title but different keys can come under same column
        // Create transformed object with all columns guaranteed
        const transformed = mergeRowValuesByTitle(columnConfigs, enriched);

        if (node.content && node.content.length > 0) {
            transformed.children = enrichBatterySystem(columnConfigs, node.content, level + 1);
        }
        return transformed;
    });
};

const InstantaneousMatrix = ({ batteryID, batterySystem, batteryInfo, vehicleInfo = {}, showBusDeviceInfo = () => { } }) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const { t } = useTranslation(['common', 'generic_table']);
    const pageContent = usePagesContent();
    // Device ID label: a battery org's baseEntity.idLabel (e.g. "IMEI"); a vehicle org's
    // idLabel names the plate, so its device ID keeps "Device ID".
    const entityIdLabel = resolveDeviceIdLabel(useGlobalConfig(), t('common:device_id'));
    // Plate label: baseEntity.idLabel, else "Bus Number".
    const vehicleIdLabel = useBaseEntityIdLabel(t('digital_twin:section_ids.bus_number'));
    const [allTableColumns, setAllTableColumns] = useState([]);
    const [tableColumns, setTableColumns] = useState(allTableColumns);
    const [selectedTableColumns, setSelectedTableColumns] = useState(tableColumns.map(column => column.dataIndex));
    const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
    const [tableData, setTableData] = useState([]);

    const getEntityTypeList = () => {
        let clientEntityTypeList = pageContent?.metaInfo?.hierarchyEntityTypes || false
        let defaultEntityTypeList = 'BATTERY_SYSTEM'
        if (clientEntityTypeList) {
            defaultEntityTypeList = clientEntityTypeList[0]
        }
        return defaultEntityTypeList;
    }

    const loadColumnData = async () => {
        const [batteryColumnsResponse, batteryColumnsDataResponse] = await Promise.all([
            getBatteryColumns(batteryID),
            getBatteryColumnsData(batteryID, getEntityTypeList()),
        ]);
        const columns = batteryColumnsResponse?.response?.data?.batteryColumns ?? [];
        const columnOrder = batteryColumnsResponse?.response?.data?.columnOrder ?? [];

        const formattedColumns = columns.reduce((acc, curr) => {
            acc[curr.dataIndex] = curr;
            return acc;
        }, {});

        // adding degree symbol for temperature and also replacing key/dataIndex by title
        // key/dataIndex are replaced by title so that attributes with same label but
        // different keys can be rendered under the same table column
        const columnsWithReplacedKeys = Object.fromEntries(
            Object.entries(formattedColumns).map(([key, value]) => {
                return [
                    key,
                    {
                        ...value,
                        title: value.title.replace(/\(\s*C\s*\)/g, "(°C)"),
                        key: value.title,
                        dataIndex: value.title,
                    }
                ]
            })
        );

        const columnsConfig = getColumnsConfig({
            tableColumns: columnsWithReplacedKeys,
            tableColumnOrder: columnOrder,
            customRenderers,
            t
        });

        // remove duplicates
        let uniqueColumnsConfig = columnsConfig.filter((item, index, self) =>
            index === self.findIndex((t) => t.dataIndex === item.dataIndex)
        );

        // sort by fixed key and then pick the first MAX_COLUMN_SELECTION_COUNT columns
        const defaultColumns = uniqueColumnsConfig.sort((a, b) => {
            if (a.fixed && !b.fixed) return -1;
            if (!a.fixed && b.fixed) return 1;
            return 0;
        }).slice(0, MAX_COLUMN_SELECTION_COUNT);

        setAllTableColumns(uniqueColumnsConfig);
        setTableColumns(defaultColumns);
        setSelectedTableColumns(defaultColumns.map(column => column.dataIndex));
        setDefaultSelectedColumns(defaultColumns.map(column => column.dataIndex));

        const columnsData = batteryColumnsDataResponse?.response?.data?.batteryInfo ?? [];
        setTableData(enrichBatterySystem(formattedColumns, columnsData));
    }

    useEffect(() => {
        loadColumnData();
    }, [batteryInfo]);

    const updateSelectedTableColumns = (newSelectedColumns) => {
        const filteredColumns = allTableColumns.filter((columnConfig) => {
            return newSelectedColumns.includes(columnConfig.dataIndex)
        });
        setTableColumns(filteredColumns);
        setSelectedTableColumns(newSelectedColumns);
    }

    const onExpand = (expanded, record) => {
        const keys = [...expandedRowKeys];
        if (expanded) {
            keys.push(record.key);
        } else {
            const index = keys.indexOf(record.key);
            if (index > -1) {
                keys.splice(index, 1);
            }
        }
        setExpandedRowKeys(keys);
    };
    const ExpandIconComponent = ({ expanded, onExpand, record }) => {
        if (!record.children || record.children.length == 0) {
            return null
        }
        return (
            <IconButton
                disableRipple
                disableTouchRipple
                disableFocusRipple
                sx={{
                    fontSize: '16px', paddingLeft: 0, backgroundColor: 'transparent !important',
                    '&:hover': { backgroundColor: 'transparent !important' }
                }} onClick={e => onExpand(record, e)}>
                <CustomIcon name="primary-arrow-icon" rotate={expanded ? 90 : 0} />
            </IconButton >
        )
    }
    if (!batterySystem || batterySystem.length === 0) {
        return <CartLoader />
    }

    const lottieOptions = {
        loop: true,
        autoplay: true,
        animationData: PurpleBlinkingLottie,
        rendererSettings: {
            preserveAspectRatio: "xMidYMid slice"
        }
    };
    const tableTitle = <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <Lottie
            options={lottieOptions}
            height={24}
            width={24}
        />
        <>Instantaneous Value Comparison</>
    </Box>;
    // The org's batteryVitals.idDisplay.battery, else the plate or device ID; hidden when empty.
    const idDisplay = resolveBatteryIdDisplay({
        idDisplayConfig: getVitalsIdDisplayConfig(pageContent, 'batteryVitals', 'battery'),
        batteryInfo,
        vehicleInfo,
        showVehicleIdentifier: showBusDeviceInfo() === true,
        vehicleIdLabel,
        deviceIdLabel: entityIdLabel,
    });
    const customRightActionsView = idDisplay.value === null ? null : (
        <IDDisplayBox>
            <Box>
                <Typography variant="fw400s12c494949">{idDisplay.label}:&nbsp;</Typography>
                <Typography variant="fw400s12c494949">{idDisplay.value}</Typography>
            </Box>
        </IDDisplayBox>
    )

    return (
        <TableContainer>
            <GenericDraggableTableView
                tableID={'InstantaneousMatrix'}
                title={tableTitle}
                containerStyles={{ width: 'auto' }}
                disableSearch={true}
                disableDownloads={true}
                showPrimaryButton={false}
                tableColumns={tableColumns}
                tableData={tableData}
                isLoading={false}
                disablePagination={true}
                emptyMessage="No data found"
                customRightActions={true}
                customRightActionsView={customRightActionsView}
                expandable={{
                    expandedRowKeys,
                    onExpand,
                    expandIcon: ExpandIconComponent,
                }}
                initialColumns={allTableColumns}
                selectedColumns={selectedTableColumns}
                applySelectedColumns={updateSelectedTableColumns}
                disableFiltersSettings={true}
                maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
                defaultSelectedColumns={defaultSelectedColumns}
            />
        </TableContainer>
    );
};
export default InstantaneousMatrix;
